/**
 * ─── VENDORED FROM arrow-fe ────────────────────────────────────────────────
 *
 * Source of truth: `arrow-fe/src/lib/lib/api/tokens.ts`.
 *
 * This is a copy, not a fork. The panel speaks the same sealed-envelope
 * protocol as the storefront (docs/backend/ENCRYPTION.md), and two
 * implementations of a security protocol that drift is a real liability — so
 * changes belong upstream first, then get copied here.
 *
 * If this ever needs to diverge from the storefront's copy, that is the signal
 * to stop copying and extract a shared workspace package instead
 * (ADMIN.md §4). Do not "fix" it here in isolation.
 * ───────────────────────────────────────────────────────────────────────────
 */

/**
 * The access token, held in memory only.
 *
 * **Not `localStorage`.** A token in local storage is readable by any script
 * that ends up on the page — one bad dependency, one injected tag — and it
 * survives the tab, so a stolen copy stays valid for its full fifteen minutes
 * after the victim has closed the browser. In memory it dies with the tab.
 *
 * Losing it on refresh is not a problem, because it is not what keeps a session
 * alive: the refresh cookie does that. It is `HttpOnly` and scoped to
 * `/v1/auth`, so no script can read it and no other endpoint ever receives it.
 * On a cold page load the client calls `POST /v1/auth/refresh` once and is
 * signed in again — which is also why a stolen access token is worth so much
 * less than a stolen refresh cookie.
 *
 * Deliberately dependency-free. `api-client.ts` imports this, and this must
 * never import back.
 */

let accessToken: string | null = null;

type Listener = (token: string | null) => void;

const listeners = new Set<Listener>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  if (accessToken === token) return;

  accessToken = token;
  for (const listener of listeners) listener(token);
}

export function clearAccessToken(): void {
  setAccessToken(null);
}

/**
 * Notifies on every change. The auth context uses this so that a token
 * refreshed deep inside `apiFetch` — which the UI never called directly —
 * still updates what the header renders.
 */
export function onAccessTokenChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
